import { useApiCall } from '@/hooks/useApiCall';
import { FunctionComponent } from 'react';
import { eventService } from '@/modules/events/api/events/event.service';
import { EventModel } from '@/modules/events/types/EventModel';
import { AssetEventsGrid } from './AssetEventsGrid';
import { PagePaginationResultDto, PageSortOrder } from '@/lib/api/pagination.page.dto';
import { EVENT_FETCH_LIMIT } from '@/modules/events/pages/EventsPage';
import { EventSortOption } from '@/modules/events/api/events/event.contracts';
import PaginationControls from '@/components/grid/PaginationControls';
import PagedResultDataText from '@/components/filterbar/PagedResultDataText';
import { useTranslation } from '@/lib';
import { useParams } from 'react-router';
import { useExportToExcel } from '@/hooks/useExportToExcel';
import { GridApi, GridReadyEvent, SortChangedEvent } from 'ag-grid-community';
import dayjs from 'dayjs';
import { formatGpsAccuracy } from '@/components/grid/cells/GpsAccuracyCellRenderer';
import { useDate } from '@/hooks/useDate';
import { ColumnID } from '@/components/grid/column-ids';
import useQueryParamsFilters from '@/hooks/useQueryParamFilters';
import { CacheKey } from '@/providers/cache-provider/cache-key.enum';
import { FilterValues } from '@/components/filterbar/FilterBarContext';
import ErrorLoadingDataAlert from '@/components/feedback/ErrorLoadingDataAlert';

export const AssetEventsTab: FunctionComponent = () => {
  const { t } = useTranslation();
  const { formatDateTimestamp } = useDate();
  const { assetId } = useParams();
  if (!assetId) {
    return;
  }

  const assetIdNumber = assetId ? Number.parseInt(assetId) : 0;
  const { exportToExcel } = useExportToExcel();

  const { filters, setFiltersToUrl } = useQueryParamsFilters(
    {
      pageNumber: 1,
      sortOption: EventSortOption.OCCURRED_AT,
      sortDirection: PageSortOrder.DESC,
    },
    CacheKey.ASSET_EVENT_FILTERS,
  );

  const {
    data: events,
    isLoading,
    isError,
    setApiCallArg,
  } = useApiCall<PagePaginationResultDto<EventModel>>(() =>
    eventService.get(
      { assetId: assetIdNumber },
      {
        page: filters.current.pageNumber ?? 1,
        limit: EVENT_FETCH_LIMIT,
        order: filters.current.sortDirection ?? PageSortOrder.DESC,
        sort: filters.current.sortOption ?? EventSortOption.OCCURRED_AT,
      },
    ),
  );

  const handleRefresh = (filterValues: FilterValues, page?: number) => {
    setFiltersToUrl({
      ...filterValues,
      pageNumber: page ?? filters.current.pageNumber,
    });

    setApiCallArg(() =>
      eventService.get(
        {
          assetId: assetIdNumber,
        },
        {
          page: filters.current.pageNumber,
          limit: EVENT_FETCH_LIMIT,
          order: filters.current.sortDirection,
          sort: filters.current.sortOption,
        },
      ),
    );
  };

  const onSortChanged = (event: SortChangedEvent) => {
    if (event.columns && event.columns.length > 0 && event.source === 'uiColumnSorted') {
      const changedColumn = event.columns.find((col) => col.getSort());
      if (!changedColumn) {
        handleRefresh(
          {
            ...filters.current,
            sortOption: undefined,
            sortDirection: undefined,
          },
          1,
        );
        return;
      }

      const changedColumnId = changedColumn.getColId();
      let newSortOption: EventSortOption | undefined = undefined;

      switch (changedColumnId) {
        case ColumnID.ASSET_CODE:
          newSortOption = EventSortOption.ASSET_CODE;
          break;
        case ColumnID.LOCATION_NAME:
          newSortOption = EventSortOption.LOCATION_NAME;
          break;
        case ColumnID.OCCURRED_AT:
          newSortOption = EventSortOption.OCCURRED_AT;
          break;
        case ColumnID.TRACKER_TYPE:
          newSortOption = EventSortOption.TRACKER_TYPE;
          break;
        case ColumnID.EVENT_TYPE:
          newSortOption = EventSortOption.EVENT_TYPE;
          break;
        case ColumnID.GPS_ACCURACY:
          newSortOption = EventSortOption.GPS_ACCURACY;
          break;
        case ColumnID.GPS_SOURCE_TYPE:
          newSortOption = EventSortOption.GPS_SOURCE_TYPE;
          break;
        case ColumnID.EVENT_SOURCE:
          newSortOption = EventSortOption.EVENT_SOURCE;
          break;
        default:
          break;
      }

      let newSortDirection: PageSortOrder | undefined = undefined;

      switch (changedColumn.getSort()) {
        case 'asc':
          newSortDirection = PageSortOrder.ASC;
          break;
        case 'desc':
          newSortDirection = PageSortOrder.DESC;
          break;
        default:
          newSortOption = undefined; // If there is no sort direction, remove the sort option
          break;
      }

      // Apply the sorting so only one column can be sorted at a time
      event.api.applyColumnState({
        state: [
          {
            colId: changedColumnId,
            sort: newSortDirection === PageSortOrder.ASC ? 'asc' : 'desc',
          },
        ],
        defaultState: { sort: null },
      });

      // Refresh data with new sort (reset to page 1)
      handleRefresh(
        {
          ...filters.current,
          sortOption: newSortOption,
          sortDirection: newSortDirection,
        },
        1,
      );
    }
  };

  const onGridReady = (event: GridReadyEvent) => {
    // Set initial sort state to grid
    let columnId: ColumnID = ColumnID.OCCURRED_AT;
    const sortOption = filters.current.sortOption ?? EventSortOption.OCCURRED_AT;
    const sortDirection = filters.current.sortDirection ?? PageSortOrder.DESC;

    switch (sortOption) {
      case EventSortOption.ASSET_CODE:
        columnId = ColumnID.ASSET_CODE;
        break;
      case EventSortOption.LOCATION_NAME:
        columnId = ColumnID.LOCATION_NAME;
        break;
      case EventSortOption.OCCURRED_AT:
        columnId = ColumnID.OCCURRED_AT;
        break;
      case EventSortOption.TRACKER_TYPE:
        columnId = ColumnID.TRACKER_TYPE;
        break;
      case EventSortOption.EVENT_TYPE:
        columnId = ColumnID.EVENT_TYPE;
        break;
      case EventSortOption.GPS_ACCURACY:
        columnId = ColumnID.GPS_ACCURACY;
        break;
      case EventSortOption.GPS_SOURCE_TYPE:
        columnId = ColumnID.GPS_SOURCE_TYPE;
        break;
      case EventSortOption.EVENT_SOURCE:
        columnId = ColumnID.EVENT_SOURCE;
        break;
      default:
        break;
    }

    event.api.applyColumnState({
      state: [
        {
          colId: columnId,
          sort: sortDirection === PageSortOrder.ASC ? 'asc' : 'desc',
        },
      ],
      defaultState: { sort: null },
    });
  };

  const onPageChanged = (event: React.ChangeEvent<unknown>, page: number) => {
    handleRefresh(
      {
        ...filters.current,
        pageNumber: page,
      },
      page,
    );
  };

  const onExportToExcelClicked = async (gridApi: GridApi<EventModel> | undefined) => {
    if (gridApi) {
      const data: EventModel[] = [];

      // Fetch all pages of data for export
      const dataPage1 = await eventService.get(
        { assetId: assetIdNumber },
        {
          page: 1,
          limit: EVENT_FETCH_LIMIT,
          order: PageSortOrder.DESC,
          sort: EventSortOption.OCCURRED_AT,
        },
      );

      if (dataPage1.isSuccess) {
        data.push(...dataPage1.payload.data);
      }

      // Fetch additional pages if needed
      for (let i = 2; i <= (dataPage1.payload.totalPages || 1); i++) {
        const dataPage = await eventService.get(
          { assetId: assetIdNumber },
          {
            page: i,
            limit: EVENT_FETCH_LIMIT,
            order: PageSortOrder.DESC,
            sort: EventSortOption.OCCURRED_AT,
          },
        );

        if (dataPage.isSuccess) {
          data.push(...dataPage.payload.data);
        }
      }

      if (data.length > 0) {
        exportToExcel(
          data.map((event) => ({
            assetCode: event.asset.code,
            occurredAt: formatDateTimestamp(event.occurredAt),
            location: event.location?.name,
            eventType: t(`event.type.${event.type}`),
            eventSource: t(`enums.event_source.${event.eventSource}`),
            gpsAccuracy: event.gpsAccuracyLevel ? formatGpsAccuracy(event.gpsAccuracyLevel, t) : t('unknown'),
            gpsSourceType: t(`event.gps_source.${event.gpsSourceType}`),
          })),
          `asset_${data[0].asset.code}_events_${dayjs().format('YYYY_MM_DD_HHmmss')}.xlsx`,
        );
      }
    }
  };

  return (
    <div className="flex h-full flex-1 grow flex-col ">
      {isError ? (
        <ErrorLoadingDataAlert />
      ) : (
        <>
          <AssetEventsGrid
            data={events?.data}
            isError={isError}
            isLoading={isLoading}
            onExportToExcelClicked={onExportToExcelClicked}
            onSortChanged={onSortChanged}
            onGridReady={onGridReady}
          />
          <div className="flex items-center gap-x-4">
            <PaginationControls
              isLoading={isLoading}
              totalPageCount={events?.totalPages ?? 1}
              currentPage={filters.current.pageNumber ?? 1}
              totalElements={events?.totalElements ?? 0}
              onChange={onPageChanged}
            />
            <PagedResultDataText data={events} name={t('event.event', { count: events?.totalElements })} />
          </div>
        </>
      )}
    </div>
  );
};
